<template>
	<form-element v-bind="$props" :message="messageComputed" :valid="valid" :error="error">
		<input
			v-if="!textarea"
			:id="id"
			ref="element"
			class="form-input"
			:type="type"
			:name="name"
			:value="modelValue"
			:placeholder="placeholder"
			:autocomplete="autocomplete"
			:required="required ? true : null"
			:data-rule="rule"
			:data-look="look"
			:data-mask="mask"
			@input="changeHandler"
			@change="changeHandler"
		/>
		<textarea 
			v-else
			:id="id"
			ref="element"
			class="form-textarea"
			:name="name"
			:value="modelValue"
			:placeholder="placeholder"
			:autocomplete="autocomplete"
			:required="required ? true : null"
			data-rule="skip"
			@input="changeHandler"
			@change="changeHandler"
		/>
		<div v-if="required" class="form-element__required-placeholder">{{ placeholder }}</div>
	</form-element>
</template>

<script lang="ts">
import { mapActions } from 'pinia';
import { classInstance } from '@/shared/helpers/helpers';

import FormElement from './form-element.vue';

import { useElementChange, useElementComplete } from '@/shared/form/form-element/composition/elementEvents';
import { useGetValid } from '@/shared/form/form-element/form-input/composition/getValid';
import { useAppendMask } from '@/shared/form/form-element/form-input/composition/appendMask';
import { useRemoveMask } from '@/shared/form/form-element/form-input/composition/removeMask';
import { useInputsStore } from '@/components/order-vue/stores/inputs.js';

export default {
	name: 'FormInput',
	
	components: {
		FormElement,
	},
	
	props: {
		id: { type: String, default: '' },
		type: { type: String, default: 'text' },
		name: { type: String, default: '' },
		textarea: { type: Boolean, default: false },
		modelValue: { type: String, default: '' },
		label: { type: String, default: '' },
		message: { type: String, default: '' },
		describe: { type: String, default: '' },
		placeholder: { type: String, default: '' },
		autocomplete: { type: String, default: '' },
		required: { type: Boolean, default: false },
		readonly: { type: Boolean, default: false },
		disabled: { type: Boolean, default: false },
		look: { type: Boolean, default: false },
		rule: { type: String, default: 'text' },
		mask: { type: String, default: '' },
		icon: { type: String, default: '' },
		inputMode: { type: String, default: '' }
	},
	
	emits: ['update:modelValue'],
	
	data() {
		return {
			valid: false,
			error: false,
			messageComputed: '',
		};
	},
	
	methods: {
		...mapActions(
			useInputsStore,
			{
				addInput: 'add',
				removeInput: 'remove',
				setValid: 'setValid',
			}
		),
		
		validate(options: OptionsVE = {}): boolean {
			const optionsValidate: OptionsVE = {
				noRender: options.noRender || false,
				noEmpty: options.noEmpty || !this.required,
				rule: options.rule || this.rule,
				message: options.message || this.message || this.rule || this.type,
			};

			const result: ResultVE = useGetValid(this.$refs.element, optionsValidate);

			if (result.message) this.messageComputed = result.message;
			
			if (result.valid && !result.error) {
				useElementComplete(this.$refs.element, { value: this.modelValue });
			}
			
			if (!optionsValidate.noRender) {
				this.valid = result.valid;
				this.error = result.error;
			}
			
			if (this.required) {
				this.setValid(this.$refs.element, result.valid);
			}
			
			return result.valid;
		},

		changeHandler(event: InputEvent): void {
			this.validate();
			const $target: HTMLInputElement = event.target as HTMLInputElement;
			this.$emit('update:modelValue', $target.value);
			setTimeout(() => useElementChange(this.$refs.element, { value: this.modelValue }));
		},
	},

	mounted() {
		this.addInput(this.$refs.element);
		classInstance.set(this.$refs.element, { formElement: this });
		if (this.mask) useAppendMask(this.$refs.element);
		if (this.modelValue) this.validate();
	},

	beforeUnmount() {
		this.removeInput(this.$refs.element);
		classInstance.del(this.$refs.element, 'formElement');
		if (this.mask) useRemoveMask(this.$refs.element);
	},
};
</script>
